import type { FeatureLike } from "ol/Feature"
import type { Pixel } from "ol/pixel"
import type OlMap from "ol/Map"

export const useSelectedFeatures = (map: Ref<OlMap | null>, currentZoom: Ref<number>, clusterZoomLevel: number) => {
  const selectedFeatures: Ref<Array<FeatureLike>> = ref([]) // Selected features on the map

  const getFeatures = (pixel: Pixel) => {
    if (!map.value) return []

    const view = map.value!.getView()

    const features = map.value.getFeaturesAtPixel(pixel, {
      layerFilter: (layer) =>
        layer.isVisible(view) &&
        [OpenLayersLayer.MARKER, OpenLayersLayer.MARKER_CLUSTER, OpenLayersLayer.POSITION].includes(layer.get("name")),
    })

    if (!features.length) return []

    return features
  }

  const initializeSelectedFeatures = () => {
    if (!map.value) throw new Error("Map is not initialized")

    map.value.on("click", (event) => {
      const view = map.value!.getView()

      const features = getFeatures(event.pixel)
      const clusters = features.filter((feature) => feature.get("type") === OpenLayersLayer.MARKER_CLUSTER)

      if (clusters.length && currentZoom.value && currentZoom.value < clusterZoomLevel) {
        const totalClusterCenter = clusters.reduce<[number, number]>(
          ([totalX, totalY], cluster) => {
            const geometry = cluster.getGeometry()

            if (!geometry || !("getCoordinates" in geometry) || typeof geometry.getCoordinates !== "function") {
              return [totalX, totalY]
            }

            const coordinates = geometry.getCoordinates() as [number, number]
            return [totalX + coordinates[0], totalY + coordinates[1]]
          },
          [0, 0],
        )

        const averageClusterCenter = [totalClusterCenter[0] / clusters.length, totalClusterCenter[1] / clusters.length]

        // Center on the click location and zoom in on the cluster
        view.setCenter(averageClusterCenter)
        view.setZoom(clusterZoomLevel + 0.01)
      } else {
        selectedFeatures.value = features
      }
    })
  }

  return {
    initializeSelectedFeatures,
    selectedFeatures,
    getFeatures,
  }
}
